import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

type ServiceItem = {
  key: string;
  name: string;
  description: string;
  path: string;
  icon: string;
  external?: boolean;
  status: "up" | "down";
  latency_ms: number | null;
};

type Me = {
  user: { display_name: string; telegram_username: string | null } | null;
  workspaces: string[];
};

// Auth is served by the tracker on this same origin (apex path router), so the
// link stays relative and survives a domain move.
const LOGIN_URL = "/auth/telegram/start?return_to=" + encodeURIComponent(window.location.origin + "/");

// Chrome's install prompt event (not in the standard TS lib).
type BeforeInstallPromptEvent = Event & { prompt: () => Promise<void> };

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    fetch("/api/me", { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) {
          setAuthed(false);
          return;
        }
        setMe((await r.json()) as Me);
        setAuthed(true);
      })
      .catch(() => setAuthed(false));
  }, []);

  if (authed === null) return <div className="splash">Загрузка…</div>;
  if (!authed) return <Login />;
  if (asking) return <Ask me={me} onBack={() => setAsking(false)} />;
  return <Launcher me={me} onAsk={() => setAsking(true)} />;
}

// ---- gate --------------------------------------------------------------------

function Login() {
  return (
    <div className="splash">
      <div className="login-card">
        <img src="/favicon.svg" alt="" width="88" height="88" />
        <h1>Medusa</h1>
        <a className="btn block" href={LOGIN_URL}>Войти через Telegram</a>
      </div>
    </div>
  );
}

// ---- launcher ----------------------------------------------------------------

function Launcher({ me, onAsk }: { me: Me | null; onAsk: () => void }) {
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const timer = useRef<number | null>(null);

  const loadServices = useCallback(() => {
    fetch("/api/services", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("unauthorized"))))
      .then((d: { items: ServiceItem[] }) => setServices(d.items))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadServices();
    timer.current = window.setInterval(loadServices, 30_000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [loadServices]);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);

  return (
    <div className="hub">
      <header className="head">
        <div className="brand">
          <img src="/favicon.svg" alt="" width="34" height="34" />
          <h1>Medusa</h1>
        </div>
        {me?.user && <UserMenu user={me.user} workspaces={me.workspaces} />}
      </header>

      <button className="ask-entry" onClick={onAsk}>
        <span className="ask-icon">✨</span>
        <span className="ask-text">
          <span className="ask-title">Спросить ассистента</span>
          <span className="ask-sub">Ответит по вики, задачам, встречам и финансам</span>
        </span>
        <span className="ask-arrow">→</span>
      </button>

      <main className="tiles">
        {services.length === 0 &&
          [0, 1, 2, 3, 4].map((i) => (
            <div className="tile skeleton" key={i}>
              <span className="tile-icon" />
              <div className="tile-text">
                <span className="tile-name">…</span>
              </div>
            </div>
          ))}
        {services.map((s) => (
          <a
            className={`tile ${s.status === "down" ? "down" : ""}`}
            key={s.key}
            href={s.path}
            {...(s.external ? { target: "_blank", rel: "noreferrer" } : {})}
          >
            <img className="tile-icon" src={s.icon} alt="" width="34" height="34" loading="lazy" />
            <div className="tile-text">
              <span className="tile-name">
                {s.name}
                {s.external && <span className="ext">↗</span>}
              </span>
              <span className="tile-desc">{s.description}</span>
            </div>
            <span
              className={`dot ${s.status}`}
              title={s.status === "up" ? `работает · ${s.latency_ms}ms` : "недоступен"}
            />
          </a>
        ))}
      </main>

      {!isStandalone && (installEvent || isIos) && (
        <footer className="install">
          {installEvent && (
            <button
              className="btn ghost"
              onClick={() => {
                void installEvent.prompt();
                setInstallEvent(null);
              }}
            >
              ⬇ Установить приложение
            </button>
          )}
          {!installEvent && isIos && (
            <button className="btn ghost" onClick={() => setShowIosHint((v) => !v)}>
              ⬇ Установить на iPhone
            </button>
          )}
          {showIosHint && (
            <p className="ios-hint">
              В Safari: <b>Поделиться</b> <span className="share-ico">⎋</span> → <b>«На экран “Домой”»</b>
            </p>
          )}
        </footer>
      )}
    </div>
  );
}

// ---- user menu (name -> logout) ----------------------------------------------

function UserMenu({
  user,
  workspaces
}: {
  user: { display_name: string; telegram_username: string | null };
  workspaces: string[];
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // The session cookie is issued by the tracker for the whole apex domain, so
  // its logout endpoint (reachable at /api/v1 on this origin) is what clears it.
  const logout = async () => {
    setBusy(true);
    try {
      await fetch("/api/v1/auth/logout", { method: "POST", credentials: "include" });
    } catch {
      /* fall through: reload shows the login screen if the cookie is gone */
    }
    window.location.replace("/");
  };

  return (
    <div className="usermenu" ref={box}>
      <button className="badge" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="avatar">{user.display_name.slice(0, 1).toUpperCase()}</span>
        <span className="badge-name">{user.display_name}</span>
        <span className={`chev ${open ? "up" : ""}`}>▾</span>
      </button>
      {open && (
        <div className="menu">
          <div className="menu-head">
            <div className="menu-name">{user.display_name}</div>
            {user.telegram_username && <div className="muted small">@{user.telegram_username}</div>}
            {workspaces.length > 0 && <div className="muted small">{workspaces.join(", ")}</div>}
          </div>
          <button className="menu-item danger" onClick={logout} disabled={busy}>
            {busy ? "Выходим…" : "Выйти"}
          </button>
        </div>
      )}
    </div>
  );
}

// ---- assistant chat -----------------------------------------------------------

type Turn = { role: "user" | "assistant"; content: string; refs?: SourceRef[]; warnings?: string[] };
type SourceRef = { key: string; title: string; url: string };

/**
 * Renders an answer, turning [[page:id]] / [[task:n]] / [[meeting:slug]] markers
 * into links. Unknown markers are dropped rather than shown raw — the model can
 * invent one, and a broken citation is worse than none.
 */
function Answer({ text, refs }: { text: string; refs: SourceRef[] }) {
  const byKey = new Map(refs.map((r) => [r.key, r]));
  const parts: ReactNode[] = [];
  const re = /\[\[([a-z]+:[^\]]+)\]\]/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = re.exec(text))) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const ref = byKey.get(match[1]!);
    if (ref) {
      parts.push(
        <a className="cite" href={ref.url} key={`r${i++}`} title={ref.title}>
          {ref.title}
        </a>
      );
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

function SearchWarnings({ warnings }: { warnings: string[] }) {
  if (!warnings.length) return null;
  return <details className="muted"><summary>Область поиска и ограничения</summary>
    {warnings.map((warning, i) => <p key={i}>{warning}</p>)}
  </details>;
}

function Ask({ me, onBack }: { me: Me | null; onBack: () => void }) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [refs, setRefs] = useState<SourceRef[]>([]);
  const [streaming, setStreaming] = useState("");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns, streaming, busy]);

  const send = async () => {
    const question = input.trim();
    if (!question || busy) return;
    setInput("");
    setError(null);
    const history = turns.slice(-6).map(({ role, content }) => ({ role, content }));
    setTurns((t) => [...t, { role: "user", content: question }]);
    setBusy(true);
    setStreaming("");
    setRefs([]);
    setWarnings([]);
    let answer = "";
    let answerRefs: SourceRef[] = [];
    let answerWarnings: string[] = [];
    let completed = false;
    try {
      const res = await fetch("/assistant/api/v1/ask/stream", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ question, history })
      });
      if (!res.ok || !res.body) throw new Error("Не удалось получить ответ");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop() ?? "";
        for (const frame of frames) {
          const evLine = frame.split("\n").find((l) => l.startsWith("event:"));
          const dataLine = frame.split("\n").find((l) => l.startsWith("data:"));
          if (!evLine || !dataLine) continue;
          const event = evLine.slice(6).trim();
          const payload = JSON.parse(dataLine.slice(5).trim());
          if (event === "refs") { answerRefs = payload as SourceRef[]; setRefs(answerRefs); }
          else if (event === "warnings") { answerWarnings = payload as string[]; setWarnings(answerWarnings); }
          else if (event === "done") completed = true;
          else if (event === "delta") {
            answer += payload as string;
            setStreaming(answer);
          } else if (event === "error") throw new Error((payload as { message: string }).message);
        }
      }
      if (!completed) throw new Error("Ответ прервался. Повторите запрос.");
      if (!answer) throw new Error("Ассистент не вернул ответ. Повторите запрос.");
      setTurns((t) => [...t, { role: "assistant", content: answer, refs: answerRefs, warnings: answerWarnings }]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Ошибка");
    } finally {
      setStreaming("");
      setBusy(false);
    }
  };

  const samples = [
    "Что мы решали на последних встречах?",
    "Какие задачи в работе и на ком?",
    "Найди операции по оплате сервера"
  ];

  return (
    <div className="hub ask-view">
      <header className="head">
        <button className="ask-back" onClick={onBack}>← Все приложения</button>
        {me?.user && <span className="muted">{me.user.display_name}</span>}
      </header>

      <div className="chat">
        {turns.length === 0 && !streaming && (
          <div className="chat-empty">
            <div className="chat-empty-icon">✨</div>
            <p>Спросите что угодно про работу команды — отвечу по вашим вики, задачам, встречам и финансам.</p>
            <div className="chat-samples">
              {samples.map((s) => (
                <button key={s} onClick={() => setInput(s)}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {turns.map((t, i) =>
          t.role === "user" ? (
            <div className="bubble user" key={i}>{t.content}</div>
          ) : (
            <div className="bubble assistant" key={i}>
              <Answer text={t.content} refs={t.refs ?? []} />
              <SearchWarnings warnings={t.warnings ?? []} />
            </div>
          )
        )}
        {streaming && (
          <div className="bubble assistant">
            <Answer text={streaming} refs={refs} />
            <span className="caret" />
            <SearchWarnings warnings={warnings} />
          </div>
        )}
        {busy && !streaming && <div className="bubble assistant thinking">Думаю…</div>}
        {error && <div className="chat-error">{error}</div>}
        <div ref={endRef} />
      </div>

      <div className="chat-input">
        <textarea
          value={input}
          placeholder="Ваш вопрос…"
          rows={1}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <button className="btn" onClick={() => void send()} disabled={busy || !input.trim()}>
          {busy ? "…" : "Спросить"}
        </button>
      </div>
    </div>
  );
}
